import { inject, observer } from "mobx-react";
import * as React from "react";
import { compose } from "recompose";

import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

import {
    AppBar,
    createStyles,
    CssBaseline,
    Tab,
    Tabs,
    Theme,
    withStyles,
    withWidth
} from "@material-ui/core";

import { fade } from "@material-ui/core/styles/colorManipulator";
import { Breakpoint } from "@material-ui/core/styles/createBreakpoints";

import {AppIconType} from "../shared/AppIconType";

import { RefObject } from "react";
import contextStore from "../../stores/ContextStore";
import navigationStore from "../../stores/NavigationStore";

import { PredefinedMeetingState, ChromeState, KeyDownMessage } from "meet-addons-sdk";

import { AddonRuntimeInfo } from "../../domain/api/addons/AddonRuntimeInfo";
import { INotificationStoreProps } from "../../../src/stores/INotificationStoreProps";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import Feedback from "../home/Feedback";
import ShareInvite from "../home/ShareInvite";
import TooltipIconButton from "../shared/TooltipIconButton";
import AddonLoader from "../workspace/AddonLoader";
import Toast from "../shared/Toast";

import { PredefinedRole } from "../../domain/auth/PredefinedRole";
import meetingService from "../../services/api/MeetingDataService";
import signaling from "../../services/site/signaling";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import ConsentDialog from "../shared/ConsentDialog";

import { PredefinedHostType } from "../../domain/api/addons/PredefinedHostType";
import i18n from "../../services/localization/i18n";
import MiniBar from "./MiniBar";
import TeamsSearch from "../home/TeamsSearch";
import { IPanelStoreProps } from "../../stores/IPanelStoreProps";
import { getLocalizedText, isTeamsAddonMode } from "../../utils";
import { PredefinedAchievementScenario } from "../../services/api/PredefinedAchievementScenario";
import { AchivementsRequest } from "../../services/api/AchievementsRequest";
import { PredefinedAchievementCheckpoint } from "../../services/api/PredefinedAchievementCheckpoint";
import achievementsService from "../../services/api/AchievementsService";
import { ClientTelemetryEvent } from "../../services/tx/ClientTelemetryEvent";
import TelemetryDataService from "../../services/tx/TelemetryDataService";
import Snapshots from "../snapshots/Snapshots";
import { observe } from "mobx";

const drawerWidth = 240;
const SIDEBAR_SETTINGS = "layout-sidebar-open-2019628";


const styles = (theme: Theme) => createStyles({

    appBarSpacer: theme.mixins.toolbar,
    avatar: {
        margin: 10,
    },
    button: {
        margin: theme.spacing(),
    },
    content: {
        [ theme.breakpoints.down("sm")]: {
            marginTop: theme.spacing(10),
        },
        backgroundColor: theme.palette.background.default,
        flexGrow: 1,
        overflow: "hidden",
    },
    drawerIconImage: {
        height: 24,
        marginLeft: 8,
    },
    drawerPaper: {
        position: "relative",
        transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.enteringScreen,
            easing: theme.transitions.easing.sharp,
        }),
        whiteSpace: "nowrap",
        width: drawerWidth,
        [theme.breakpoints.up("md")]: {
            position: "relative",
        },
    },

    drawerPaperClose: {
        overflowX: "hidden",
        transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.leavingScreen,
            easing: theme.transitions.easing.sharp,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up("sm")]: {
            width: theme.spacing(9),
        },
    },

    grow: {
        flexGrow: 1,
    },
    iconSmall: {
        fontSize: 20,
    },
    inputInput: {
        paddingBottom: theme.spacing(),
        paddingLeft: theme.spacing(10),
        paddingRight: theme.spacing(),
        paddingTop: theme.spacing(),
        transition: theme.transitions.create("width"),
        width: "100%",
        [theme.breakpoints.up("sm")]: {
          "&:focus": {
            width: 220,
          },
          "width": 160,
        },
      },
    inputRoot: {
        color: "inherit",
        width: "100%",
      },

    leftIcon: {
        marginRight: theme.spacing(),
    },
    logo: {
        marginRight: 8,
        maxHeight: 32,
    },

    navIconHide: {
        [theme.breakpoints.up("md")]: {
            display: "none",
        },
    },

    rightIcon: {
        marginLeft: theme.spacing(),
    },
    root: {
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        height: "100%",
        overflow: "hidden",
        position: "relative",
        width: "100%",
        zIndex: 1,
    },
    search: {
        "&:hover": {
            backgroundColor: fade(theme.palette.common.white, 0.25),
        },
        "backgroundColor": fade(theme.palette.common.white, 0.15),
        "borderRadius": theme.shape.borderRadius,
        "marginLeft": theme.spacing(),
        "position": "relative",
        "width": "auto",
    },
    searchIcon: {
        alignItems: "center",
        display: "flex",
        height: "100%",
        justifyContent: "center",
        pointerEvents: "none",
        position: "absolute",
        width: theme.spacing(9),
    },
    sidebar: {
        "&:hover": {
            backgroundColor: theme.palette.background.default,
        },
        "backgroundColor": fade(theme.palette.background.default, 0.9),
        borderColor: theme.palette.secondary.main,
        borderStyle: "solid",
        borderWidth: "1",
        width: theme.spacing(45),
        position: "absolute",
        top: theme.spacing(13),
        bottom: theme.spacing(),
        right: theme.spacing()
    },

    tabRoot: {
        flexGrow: 1,
        minWidth: 0,
    },
    tabs: {
        paddingBottom: 1,
    },
    title: {
        display: "none",
        [theme.breakpoints.up("sm")]: {
            display: "block",
        },
    },

    toolbar: {
        paddingRight: 24, // keep right padding when drawer closed

    },
    toolbarIcon: {
        alignItems: "center",
        display: "flex",
        justifyContent: "flex-end",
        padding: "0 8px",
        ...theme.mixins.toolbar,
    },

});

export interface ILayoutProps {
    classes?: {
        appBarSpacer: string;
        avatar: string;
        button: string;
        content: string;
        container: string;
        drawerIconImage: string;
        drawerPaper: string;
        drawerPaperClose: string;
        endInterviewButton: string;
        grow: string;
        iconSmall: string;
        inputRoot: string;
        inputInput: string;
        leftIcon: string;
        logo: string;
        margin: string;
        menuButton: string;
        menuButtonHidden: string;
        navIconHide: string;
        rightIcon: string;
        root: string;
        search: string;
        searchIcon: string;
        sidebar: string;
        tabs: string;
        tabRoot: string;
        toolbar: string;
        toolbarIcon: string;
    };
    theme?: Theme;
    width?: Breakpoint;
}

export interface ILayoutState {
    feedback: boolean;
    shareInvite: boolean;

    open: boolean;

    hamburgerTooltip: boolean;
    inviteTooltip: boolean;
    sonarTooltip: boolean;
    feedbackTooltip: boolean;
    fullscreenEnterTooltip: boolean;
    fullscreenLeaveTooltip: boolean;
    endMeetTooltip: boolean;
    timeTooltip: boolean;

    callTooltip: boolean;
    codeTooltip: boolean;
    drawingTooltip: boolean;
    documentsTooltip: boolean;
    settingsTooltip: boolean;

    ending: boolean;

    /**
     * True if there is a suggestion to be shown to user to end the meeting.
     *
     * @type {boolean}
     * @memberof ILayoutState
     */
    endMeetSuggested: boolean;

    consentDialogVisible: boolean;

    teamsSearchDialogVisible: boolean;

    settingsDialogVisible: boolean;

    snapshotTake: boolean;
    snapshotsVisible: boolean;
}

@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.CONFIG)
@inject(PredefinedStoreName.NOTIFICATION)
@inject(PredefinedStoreName.PANEL)
@observer
class Layout extends React.Component <ILayoutProps &
                                      INotificationStoreProps &
                                      IContextStoreProps &
                                      IConfigStoreProps &
                                      IPanelStoreProps, ILayoutState> {

    private mainRef: RefObject<HTMLElement>;
    private lastTooltipRequest?: number;

    private sonarInTimeout?: number;
    private sonarOutTimeout?: number;
    private feedbackInTimeout?: number;
    private feedbackOutTimeout?: number;
    private settingInTimeout?: number;
    private settingOutTimeout?: number;
    private fullscreenInTimeout?: number;
    private fullscreenOutTimeout?: number;
    private inviteInTimeout?: number;
    private inviteOutTimeout?: number;
    private menuInTimeout?: number;
    private menuOutTimeout?: number;
    private callInTimeout?: number;
    private callOutTimeout?: number;
    private codeInTimeout?: number;
    private codeOutTimeout?: number;
    private drawingInTimeout?: number;
    private drawingOutTimeout?: number;
    private documentsInTimeout?: number;
    private documentsOutTimeout?: number;

    constructor(props: ILayoutProps & INotificationStoreProps & IContextStoreProps & IPanelStoreProps) {
        
        super(props);

        this.mainRef = React.createRef();

        const cachedSidebarState = localStorage.getItem(SIDEBAR_SETTINGS);
        let cachedState = {};
        if (cachedSidebarState) {

            cachedState =  JSON.parse(cachedSidebarState);
        }

        let activeSidebar = "orpheus";
        if (props.contextStore) {
            const sidebarAddons = props.contextStore.addonsInfo.filter((p) => p.host.type === PredefinedHostType.PANEL);
            if (!sidebarAddons.find(p => p.identifier === activeSidebar )) {
                activeSidebar = sidebarAddons[0].identifier;
            }
        }

        this.state  = {
            callTooltip: false,
            codeTooltip: false,
            consentDialogVisible: false,
            settingsDialogVisible: false,

            documentsTooltip: false,
            drawingTooltip: false,

            endMeetTooltip: false,

            feedback: false,
            feedbackTooltip: false,
            fullscreenEnterTooltip: false,
            fullscreenLeaveTooltip: false,
            timeTooltip: false,

            hamburgerTooltip: false,
            inviteTooltip: false,
            settingsTooltip: false,
            shareInvite: false,
            sonarTooltip: false,

            endMeetSuggested: false,

            teamsSearchDialogVisible: false,
            
            snapshotTake: false,
            snapshotsVisible: false,

            ...cachedState,
            open: false,

            ending: false,
        };

        signaling.onEndMeetSuggestion = () => {
            this.setState({
                consentDialogVisible: true,
                endMeetSuggested: true,
            });
        };

        observe(this.props.contextStore!, "keyDownMessage", (change) => this.handleKeyDown(change.newValue!));
    }

    public componentDidMount() {
        if (navigator.platform.indexOf("Win") > -1) {
            if (this.mainRef.current != null) {
                const ps = new PerfectScrollbar(this.mainRef.current);
                ps.update();
            }
        }
    }

    public componentWillUnmount() {
        if (this.sonarInTimeout) {
            clearTimeout(this.sonarInTimeout);
            delete this.sonarInTimeout;
        }
        if (this.sonarOutTimeout) {
            clearTimeout(this.sonarOutTimeout);
            delete this.sonarOutTimeout;
        }
        if (this.feedbackInTimeout) {
            clearTimeout(this.feedbackInTimeout);
            delete this.feedbackInTimeout;
        }
        if (this.feedbackOutTimeout) {
            clearTimeout(this.feedbackOutTimeout);
            delete this.feedbackInTimeout;
        }
        if (this.settingInTimeout) {
            clearTimeout(this.settingInTimeout);
            delete this.settingInTimeout;
        }
        if (this.settingOutTimeout) {
            clearTimeout(this.settingOutTimeout);
            delete this.settingOutTimeout;
        }

        if (this.inviteInTimeout) {
            clearTimeout(this.inviteInTimeout);
            delete this.inviteInTimeout;
        }
        if (this.inviteOutTimeout) {
            clearTimeout(this.inviteOutTimeout);
            delete this.inviteOutTimeout;
        }
        if (this.menuInTimeout) {
            clearTimeout(this.menuInTimeout);
            delete this.menuInTimeout;
        }
        if (this.menuOutTimeout) {
            clearTimeout(this.menuOutTimeout);
            delete this.menuOutTimeout;
        }
        if (this.fullscreenInTimeout) {
            clearTimeout(this.fullscreenInTimeout);
            delete this.fullscreenInTimeout;
        }
        if (this.fullscreenOutTimeout) {
            clearTimeout(this.fullscreenOutTimeout);
            delete this.fullscreenOutTimeout;
        }
        if (this.callInTimeout) {
            clearTimeout(this.callInTimeout);
            delete this.callInTimeout;
        }
        if (this.callOutTimeout) {
            clearTimeout(this.callOutTimeout);
            delete this.callOutTimeout;
        }
        if (this.codeInTimeout) {
            clearTimeout(this.codeInTimeout);
            delete this.codeInTimeout;
        }
        if (this.codeOutTimeout) {
            clearTimeout(this.codeOutTimeout);
            delete this.codeOutTimeout;
        }
        if (this.drawingInTimeout) {
            clearTimeout(this.drawingInTimeout);
            delete this.drawingInTimeout;
        }
        if (this.drawingOutTimeout) {
            clearTimeout(this.drawingOutTimeout);
            delete this.drawingOutTimeout;
        }

        if (this.documentsInTimeout) {
            clearTimeout(this.documentsInTimeout);
            delete this.documentsInTimeout;
        }
        if (this.documentsOutTimeout) {
            clearTimeout(this.documentsOutTimeout);
            delete this.documentsOutTimeout;
        }
    }

    public render() {
        const { classes } = this.props;
        const { chromeState, role} = this.props.contextStore!;

        const privilegedUser = role === PredefinedRole.POWER_USER ||
                                role === PredefinedRole.ADMIN;

        return (
            <React.Fragment>
                <div className={classes!.root}>

                    <MiniBar    onInviteShared={this.handleInviteShared}
                                onFeedbackRequested={ this.toggleFeedback }
                                onEndMeetSuggested={this.handleEndMeetSuggested}
                                onSnapshotRequested={this.handleSnapshotRequest}
                                snapshotSelected={this.state.snapshotsVisible}
                                ending={this.state.ending}
                    />
                    {
                        <main className={classes!.content} ref={this.mainRef}>
                            { chromeState !== "fullscreen" && <div  className={classes!.appBarSpacer} key="spacer" />}

                            {this.props.children }
                            
                            {contextStore.addonsInfo.filter((p) => p.host.type === PredefinedHostType.CANVAS)
                                        .map((addon, index) =>
                                            navigationStore.getRoute(addon.identifier).activated &&
                                                <AddonLoader
                                                    key={"canvas-" + index}
                                                    offset={64}
                                                    addonConfig={addon}
                                                    active={navigationStore.activeRoute === addon.identifier}  />,
                            )}
                        </main>
                    }

                    { this.renderSidebar() }

                    { this.state.snapshotsVisible && this.renderSnaphosts() }
                </div>


                <Toast />

                <Feedback   open={this.state.feedback || this.props.contextStore!.showFeedbackForm}
                            onClose={this.toggleFeedback}/>

                <ShareInvite open={!!this.state.shareInvite} onClose={this.toggleShareInvite} />

                <TeamsSearch open={this.state.teamsSearchDialogVisible} onClose={() => this.setState({
                    teamsSearchDialogVisible: !this.state.teamsSearchDialogVisible,
                })} />

                {this.showOnboardingTooltips()}

                {this.state.consentDialogVisible && !this.state.endMeetSuggested &&
                    <ConsentDialog  title=  {privilegedUser    ? i18n.t("layout_dialog_titleEnd")
                                                                : i18n.t("layout_dialog_titleLeave")
                                            }
                        description=    {privilegedUser    ? i18n.t("layout_dialog_descriptionEnd")
                                                            : i18n.t("layout_dialog_descriptionLeave")
                                        }
                        onAccept={this.endInterview}
                        onCancel={() => this.setState({
                            ending: false,
                            consentDialogVisible: false,
                            endMeetSuggested: false,
                        })}
                        open={true} />
                }

                {this.state.consentDialogVisible && this.state.endMeetSuggested &&
                    <ConsentDialog  title=  {i18n.t("layout_dialog_titleAbandon")}
                        description=    {i18n.t("layout_dialog_descriptionAbandon")}
                        onAccept={this.endInterview}
                        onCancel={() => this.setState({
                            consentDialogVisible: false,
                            endMeetSuggested: false,
                        })}
                        open={true} />
                }

                <Toast />

            </React.Fragment>
        );
    }

    private renderSnaphosts = () => {
        return <Snapshots  
            autoSnap={this.state.snapshotTake}  
            onClose={this.closeSnapshotPanel} 
        />
    }

    private closeSnapshotPanel = () => {
        this.setState({
            snapshotsVisible: false
        });
    }

    private handleInviteShared = () => {

        this.setState({ shareInvite: !this.state.shareInvite });
    }

    private handleSnapshotRequest = (selected: boolean) => {
        this.setState({ snapshotsVisible:  selected});
    }

    private handleEndMeetSuggested = () => {
        this.setState({ 
            consentDialogVisible: true,
            endMeetSuggested: false,
        });
    }

    private endInterview = () => {

        const { resourceId, resourceType, role } = this.props.contextStore!;
        const { environment, version } = this.props.configStore!;

        if (!resourceId) {
            throw new Error("No resourceId found in context");
        }

        this.setState({
            consentDialogVisible: false,
            ending: true,

        });

        const scenario = isTeamsAddonMode   ? PredefinedAchievementScenario.TEAMS_ONBOARDING 
                                            : PredefinedAchievementScenario.MEET_GENERAL;

        const achievement: AchivementsRequest  = {
            scenario: scenario,
            checkpoint: PredefinedAchievementCheckpoint.MEET_ENDED_EXPLICIT,
            resourceId: resourceId!,
            resourceType: resourceType!
        };

        achievementsService.postAsync(achievement);

        
        const txEvent = {
            correlationId: "N/A",
            environment: environment,
            name: "spa_meetApp_endIntervieew",
            sentryEventId: "N/A",
            silent: false,
            source: "meetApp",
            success: true,
            timeToComplete: 0,
            version: version,
            
        } as ClientTelemetryEvent;
        TelemetryDataService.reportAsync(txEvent);

        if (role === PredefinedRole.POWER_USER || role === PredefinedRole.ADMIN) {
            meetingService.patchAsync(resourceId, {
                state: PredefinedMeetingState.MEETING_COMPLETED,
            }).then((meeting) => {
                this.props.contextStore!.setMeetingState(meeting.state);
                this.toggleFeedback();
            });
        } else {
            this.props.contextStore!.setMeetingState(PredefinedMeetingState.MEETING_COMPLETED);
            this.toggleFeedback();
        }
    }

    private showOnboardingTooltips = () => {

        if (this.lastTooltipRequest === this.props.contextStore!.layout.tooltipRequested) {
            return;
        }

        this.lastTooltipRequest = this.props.contextStore!.layout.tooltipRequested;
        sessionStorage.setItem("last-tooltip-requested", JSON.stringify(this.lastTooltipRequest));

        const seenCoachmarks = localStorage.getItem("shell-onboarded");
        let timeout = 5000;
        if (seenCoachmarks === "v2") {
            timeout = 2000;
        }

        const highPriorityOnlyLoadRequest = this.lastTooltipRequest < 0;

        const self = this;

        this.sonarInTimeout =  window.setTimeout(() => {
            self.setState({ sonarTooltip: true });
            this.sonarOutTimeout = window.setTimeout(() => {
                self.setState({ sonarTooltip: false });
            }, 2000);

        }, 100);

        if (!highPriorityOnlyLoadRequest) {
            this.feedbackInTimeout = window.setTimeout(() => {
                self.setState({ feedbackTooltip: true });
                this.feedbackOutTimeout = window.setTimeout(() => {
                    self.setState({ feedbackTooltip: false });
                }, timeout);
            }, 700);
        }

        if (!highPriorityOnlyLoadRequest) {
            this.settingInTimeout = window.setTimeout(() => {
                self.setState({ settingsTooltip: true });
                this.settingOutTimeout = window.setTimeout(() => {
                    self.setState({ settingsTooltip: false });
                }, timeout);
            }, 950);
        }

        if (!highPriorityOnlyLoadRequest) {
            this.fullscreenInTimeout = window.setTimeout(() => {
                self.setState({
                    fullscreenEnterTooltip: true,
                    fullscreenLeaveTooltip: true,
                });
                this.fullscreenOutTimeout = window.setTimeout(() => {
                    self.setState({
                        fullscreenEnterTooltip: false ,
                        fullscreenLeaveTooltip: false,
                    });
                }, timeout);
            }, 1300);
        }

        this.inviteInTimeout = window.setTimeout(() => {
            self.setState({ inviteTooltip: true });
            this.inviteOutTimeout = window.setTimeout(() => {
                self.setState({ inviteTooltip: false });
            }, timeout);
        }, 1650);

        if (!highPriorityOnlyLoadRequest) {
            this.menuInTimeout = window.setTimeout(() => {
                self.setState({ hamburgerTooltip: true });
                this.menuOutTimeout = window.setTimeout(() => {
                    self.setState({ hamburgerTooltip: false });
                }, timeout);
            }, 2500);
        }

        if (!highPriorityOnlyLoadRequest) {
            this.callInTimeout = window.setTimeout(() => {
                self.setState({ callTooltip: true });
                this.callOutTimeout = window.setTimeout(() => {
                    self.setState({ callTooltip: false });
                }, timeout);
            }, 2650);
        }

        if (!highPriorityOnlyLoadRequest) {
            this.codeInTimeout = window.setTimeout(() => {
                self.setState({ codeTooltip: true });
                this.codeOutTimeout = window.setTimeout(() => {
                    self.setState({ codeTooltip: false });
                }, timeout);
            }, 2950);
        }

        if (!highPriorityOnlyLoadRequest) {
            this.drawingInTimeout = window.setTimeout(() => {
                self.setState({ drawingTooltip: true });
                this.drawingOutTimeout = window.setTimeout(() => {
                    self.setState({ drawingTooltip: false });
                }, timeout);
            }, 3200);
        }

        if (!highPriorityOnlyLoadRequest) {
            this.documentsInTimeout = window.setTimeout(() => {
                self.setState({ documentsTooltip: true });
                this.documentsOutTimeout = window.setTimeout(() => {
                    self.setState({ documentsTooltip: false });
                }, timeout);
            }, 3500);
        }

        localStorage.setItem("camera-preview-onboarded", "v2");
    }

    
    private toggleShareInvite = () => {
        this.setState((previousState) => ({
            shareInvite: !previousState.shareInvite,
        }));
    }

    private toggleFeedback = () => {

        const { setShowFeedbackForm } = this.props.contextStore!;

        this.setState((previousState) => ({
            feedback: !previousState.feedback,
        }), () => {
            if (!this.state.feedback) {
                setShowFeedbackForm(false);
            }
        });
    }

    private renderSidebar = () => {
        const { classes } = this.props;
        const { chromeState } = this.props.contextStore!;
        const { me } = this.props.configStore!;
        const { getActiveAddon, markAsActive, activeAddons } = this.props.panelStore!;

        const panelAddons = contextStore.addonsInfo.filter((p) => p.host.type === PredefinedHostType.PANEL);
        if (panelAddons.length === 0) {
            return <></>;
        }

        const onChange = (_: React.ChangeEvent<{}>, value: any): void => {
            markAsActive(value);
        };

        return <>
            <CssBaseline />

            <aside className={classes!.sidebar} hidden={activeAddons.length === 0}>

                { chromeState !== ChromeState.FULLSCREEN && 
                    <>
                        <div  className={classes!.appBarSpacer} key="spacer-sidebar" />
                        <AppBar position="static" color="default" elevation={4}>
                            <Tabs   value={ getActiveAddon()?.addonIdentifier }
                                    onChange={onChange}
                                    className={classes!.tabs}
                                    indicatorColor="secondary" 
                                    textColor="inherit">

                                {   panelAddons.map((addonRuntimeInfo, index) =>
                                    {
                                        const addonTitle = getLocalizedText(addonRuntimeInfo.title, me!.locale);
                                        const addonDescription = getLocalizedText(addonRuntimeInfo.description, me!.locale);

                                        return <Tab aria-label={ addonTitle }
                                            classes={{ root: classes!.tabRoot }}
                                            fullWidth={true}
                                            icon={this.getIcon(addonRuntimeInfo)}
                                            key={`panel-tab-${index}`}
                                            title={`${addonTitle}\n${addonDescription}`}
                                            value={addonRuntimeInfo.identifier} 
                                        />
                                    })
                                }                               


                            </Tabs>
                        </AppBar>
                    </>
                }
                {contextStore.addonsInfo.filter((p) => p.host.type === PredefinedHostType.PANEL)
                            . map((addon, index) => 

                            
                    <AddonLoader    key={"panel-" + index}
                                    offset={chromeState !== ChromeState.FULLSCREEN ? 114 : 0 }
                                    addonConfig={addon}
                                    active={ !!activeAddons.find(p => p.addonIdentifier === addon.identifier)}  />
                )}
            </aside>
        </>;
    }

    private getIcon(addonInfo: AddonRuntimeInfo) {
        // TODO: m1 Use icon from the manifest
        const {drawerIconImage} = this.props.classes!;

        const {me} = this.props.configStore!;

        let icon = addonInfo.host.icon;
        if (me?.theme === "dark") {
            if (addonInfo.host.iconDark) {
                icon  = addonInfo.host.iconDark;
            }
        }

        if (icon.indexOf("http") > -1) {
            return <img src={icon} alt={ getLocalizedText(addonInfo.title, me!.locale) } className={drawerIconImage} />;
        }

        return <TooltipIconButton
            button={false}
            icon={icon as AppIconType}
            placement="right"
            title = { getLocalizedText(addonInfo.title, me!.locale) }
            open={this.state.callTooltip}
        />;
    }

    
    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement> | KeyDownMessage) => {
        switch (e.keyCode) {
            case 120: // F9
                this.setState({
                    snapshotTake: true,
                    snapshotsVisible: !this.state.snapshotsVisible
                });
                break;
            default: 
                this.setState({
                    snapshotTake: false,
                });
                break;
        }
    }
}

const enhanced = compose<{}, ILayoutProps>(
    withStyles(styles, { withTheme: true }),
)(Layout);

export default withWidth()(enhanced);
